import SEO from "./SEO";
import { Section, SectionHead, FAQ, NumberedList, PageHero } from "./sections";
import { Reveal } from "./Reveal";
import { CTALink } from "./ui-kit";
import LeadForm from "./LeadForm";
import { Check, HelpCircle, Users } from "lucide-react";
import { Link } from "react-router-dom";

// Reusable use-case landing page (Start Investing / SIP / Retirement / future)
export default function LandingTemplate({
  seo,
  breadcrumbs,
  hero,
  painPoints,
  whyMatters,
  approach,
  solutions,
  calculator,
  calculatorTitle,
  howWeHelp,
  whoFor,
  faq,
  finalCta,
  audience,
}) {
  return (
    <div>
      <SEO {...seo} faq={faq} />
      <PageHero label={hero.label} title={hero.title} intro={hero.intro} breadcrumbs={breadcrumbs} />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-8 relative z-10">
        <Reveal>
          <CTALink to="#lead" variant="primary" data-testid="hero-cta">
            {hero.cta || "Book a free consultation"}
          </CTALink>
        </Reveal>
      </div>

      {/* Does this sound familiar */}
      <Section cream>
        <SectionHead label="Does this sound familiar?" title="The questions we hear most" />
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-2">
          {painPoints.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="flex items-start gap-4 py-5 border-t border-navy/10">
                <HelpCircle className="w-5 h-5 text-orange shrink-0 mt-1" strokeWidth={1.5} />
                <p className="font-sans text-base text-offblack">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Why this matters */}
      <Section>
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <SectionHead label="Why this matters" title={whyMatters.title} />
          </div>
          <div className="md:col-span-7 space-y-5">
            {whyMatters.paras.map((t, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p className="font-sans text-lg text-slate leading-relaxed">{t}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* Our approach — framework */}
      <Section dark>
        <SectionHead label="Our approach" title={approach.title} dark intro={approach.intro} />
        <NumberedList items={approach.stages} dark />
      </Section>

      {/* Possible solutions */}
      <Section cream>
        <SectionHead label="Possible solutions" title="Products that may fit" intro="Suitability depends on your goals, horizon and risk appetite. Nothing here is a recommendation." />
        <div className="grid md:grid-cols-3 gap-6">
          {solutions.map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <Link
                to={s.to}
                className="block h-full bg-white p-8 border border-navy/10 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
                data-testid={`solution-${i}`}
              >
                <h3 className="font-serif text-xl text-navy group-hover:text-orange transition-colors">{s.name}</h3>
                <p className="mt-3 font-sans text-sm text-slate leading-relaxed">{s.desc}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Calculator */}
      {calculator && (
        <Section>
          <SectionHead label="Illustrative calculator" title={calculatorTitle || "Model your plan"} intro="Move the sliders to see an illustrative projection. Results are estimates, not guarantees." />
          <Reveal>{calculator}</Reveal>
        </Section>
      )}

      {/* How we help */}
      <Section cream>
        <SectionHead label="How we help" title="A simple, guided process" />
        <div className="grid md:grid-cols-4 gap-6">
          {howWeHelp.map((h, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="h-full bg-white p-8 border border-navy/10">
                <span className="font-data text-sm text-orange">STEP {i + 1}</span>
                <h3 className="mt-3 font-serif text-lg text-navy">{h.title}</h3>
                <p className="mt-2 font-sans text-sm text-slate leading-relaxed">{h.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Who this is for */}
      <Section>
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionHead label="Who this is for" title="You'll relate if…" />
            <Users className="w-10 h-10 text-orange" strokeWidth={1.25} />
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-1">
            {whoFor.map((w, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div className="flex items-start gap-3 py-4 border-t border-navy/10">
                  <Check className="w-5 h-5 text-orange shrink-0 mt-0.5" strokeWidth={2} />
                  <p className="font-sans text-base text-offblack">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section cream>
        <SectionHead label="FAQ" title="Good questions, answered" />
        <div className="max-w-3xl">
          <FAQ items={faq} />
        </div>
      </Section>

      {/* Final CTA + Lead form */}
      <Section dark id="lead">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHead label="Next step" title={finalCta.title} dark intro={finalCta.intro} />
          </div>
          <div className="bg-white/5 border border-white/10 p-8 md:p-10">
            <LeadForm dark audience={audience} title="Book a free consultation" subtitle="Tell us where you are and we'll map the next step. No pressure, no jargon." />
          </div>
        </div>
      </Section>
    </div>
  );
}
